import { useEffect, useState, type RefObject } from 'react';

interface UseDynamicTableHeightOptions {
  minHeight?: number;
  bottomPadding?: number;
}

/**
 * Hook to calculate dynamic table height based on available viewport space
 * @param ref - Reference to the table container element
 * @param options - Configuration options
 * @returns Calculated height in pixels
 */
export const useDynamicTableHeight = (
  ref: RefObject<HTMLElement | null>,
  options: UseDynamicTableHeightOptions | number = {}
): number | undefined => {
  // Support legacy call signature where second param is just bottomPadding number
  const normalizedOptions: UseDynamicTableHeightOptions = 
    typeof options === 'number' ? { bottomPadding: options } : options;
  const { minHeight = 400, bottomPadding = 24 } = normalizedOptions;
  const [height, setHeight] = useState<number | undefined>(undefined);

  useEffect(() => {
    const calculateHeight = () => {
      if (!ref.current) return;

      const rect = ref.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const availableHeight = viewportHeight - rect.top - bottomPadding;
      
      // Ensure minimum height for at least 10 rows
      const calculatedHeight = Math.max(availableHeight, minHeight);
      
      setHeight(calculatedHeight);
    };

    // Calculate on mount and when window resizes
    calculateHeight();
    
    window.addEventListener('resize', calculateHeight);
    window.addEventListener('orientationchange', calculateHeight);

    return () => {
      window.removeEventListener('resize', calculateHeight);
      window.removeEventListener('orientationchange', calculateHeight);
    };
  }, [ref, minHeight, bottomPadding]);

  return height;
};
